import { Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';

import { submitCheckRequest } from '@modules/ContactManager/components/Tabs/components/Deals/components/CheckRequest/request/CheckRequestApi';
import { ILender } from '@modules/admin/AdminTools/Workflows/types';
import { Modal } from '@shared/components/modals/Modal';
import { SERVICES } from '@shared/constants/apiConfig';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { CommonButtonWrapper } from '@styles/CommonStyles';
import { customCheckRequestModalSchema } from '@modules/accounting/validation';
import { CheckRequestType } from '@modules/ContactManager/components/Tabs/components/Deals/interfaces';
import { useTabs } from '@shared/hooks/useTabs';
import { withQueryParams } from '@shared/commonFunctions';
import { productCheckRequestObj } from '@config/routesConfig';
import { ICustomCheckRequestFormValues, IPayeeLender, cancellationReasons, productTypes } from '../interfaces';
import { CustomCheckRequestForm } from './CustomCheckRequestForm';

interface ICheckRequestModalProps {
  customCheckRequestModalPosition: number;
  configurationSubGroup?: string;
  handleClose : () => void;
  refreshCheckRequest: React.Dispatch<React.SetStateAction<number>>;
}

export const CustomCheckRequestModal = ({
  customCheckRequestModalPosition,
  handleClose,
  refreshCheckRequest,
  configurationSubGroup,
}: ICheckRequestModalProps) => {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const [loading, setLaoding] = useState(false);

  const { data: lenders = [], error } = useSWR<ILender[]>(`/${SERVICES.LENDER}/lenders`);
  const { data: payeeData = [] }      = useSWR<IPayeeLender[]>(`/${SERVICES.LENDER}/checkrequestpayee`);

  const sortedLenderOptions = useMemo(() => lenders?.sort((a, b) => a.name.localeCompare(b.name)), [lenders]);

  const initialValues: ICustomCheckRequestFormValues = useMemo(() => ({
    customerName         : '',
    legacyDealId         : '',
    productType          : productTypes[0].value || '',
    provider             : 'NAC',
    lenderCode           : '',
    payeeName            : '',
    isPayeeLender        : false,
    address              : '',
    customerAmountRefund : 0,
    sellerAmountRefund   : 0,
    amountRequested      : 0,
    cancelledDate        : new Date(),
    achRefNumber         : '',
    reason               : cancellationReasons[0].value || '',
    requestComment       : '',
    checkRequestType     : Number(CheckRequestType.ProductCancelCheck),
    }), []); 
    const handleSubmit = useCallback(
      async (data: ICustomCheckRequestFormValues) => {
        data.achRefNumber = String(data.achRefNumber);
        setLaoding(true);
        const customCheckRequestPayload= {
          ...data,
          payeeName : `${data?.payeeName ?? ''} ${data.address ?? ''}`
        };
        const resp = await submitCheckRequest(customCheckRequestPayload);
        if (resp) {
          handleClose();
          if (configurationSubGroup === productCheckRequestObj.url.split('/')[2]) {
            refreshCheckRequest((prev) => prev + 1);
          } else {
            const route = withQueryParams(productCheckRequestObj.url, {
              queueId: productCheckRequestObj.queueId,
              queueType: productCheckRequestObj.queueType,
            });
            const title = productCheckRequestObj.text;
            openTab({ title, route });
            navigate(route);
          }
        }
        setLaoding(false);
      },
      [handleClose, productCheckRequestObj, configurationSubGroup]
    );

  return (
    <Modal
      showFooter
      hideBackdrop
      hideSave
      hideCancel
      title="Custom Check Request"
      dialogWidth="800px"
      open={!!customCheckRequestModalPosition}
      resetPosition={customCheckRequestModalPosition}
      onClose={handleClose}
    >
      <Grid
        container
        spacing={12}
      >
        <Grid
          item
          md={12}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={customCheckRequestModalSchema}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form onSubmit={props.handleSubmit}>
                <CustomCheckRequestForm
                  lenderOptions={sortedLenderOptions}
                  lendersError={!!error}
                  payeeData={payeeData}
                  {...props}
                />
                <CommonButtonWrapper direction="row">
                  <CancelButton
                    label="Cancel"
                    onClick={handleClose}
                  />
                  <SaveButton label="Create" isLoading={loading}/>
                </CommonButtonWrapper>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </Modal>
  );
};
